import { Scene, Vector3 } from '@babylonjs/core';
import { Chunk, ChunkCoord, CHUNK_SIZE, CHUNK_HEIGHT } from './Chunk';
import { VoxelMesher } from './VoxelMesher';
import { TerrainGenerator, TerrainConfig } from './TerrainGenerator';
import { Block } from './Block';

export interface ChunkManagerConfig {
  renderDistance: number;
  terrainConfig?: Partial<TerrainConfig>;
}

export class ChunkManager {
  private scene: Scene;
  private chunks: Map<string, Chunk> = new Map();
  private terrainGenerator: TerrainGenerator;
  private renderDistance: number;
  private centerChunk: ChunkCoord = { x: 0, z: 0 };
  private chunksToLoad: ChunkCoord[] = [];
  private chunksToUnload: string[] = [];
  private isProcessing: boolean = false;

  constructor(scene: Scene, config: ChunkManagerConfig) {
    this.scene = scene;
    this.renderDistance = config.renderDistance;
    this.terrainGenerator = new TerrainGenerator(config.terrainConfig);
  }

  public initialize(centerPosition: Vector3): void {
    // Force initial chunk loading by setting center and updating queue
    this.centerChunk = Chunk.worldToChunkCoord(centerPosition.x, centerPosition.z);
    this.updateChunkQueue();
  }

  public updateCenter(position: Vector3): void {
    const newCenter = Chunk.worldToChunkCoord(position.x, position.z);

    if (newCenter.x !== this.centerChunk.x || newCenter.z !== this.centerChunk.z) {
      this.centerChunk = newCenter;
      this.updateChunkQueue();
    }
  }

  private updateChunkQueue(): void {
    const { x: cx, z: cz } = this.centerChunk;
    const rd = this.renderDistance;

    // Find chunks that need to be loaded
    const neededChunks = new Set<string>();

    for (let dz = -rd; dz <= rd; dz++) {
      for (let dx = -rd; dx <= rd; dx++) {
        // Circular loading pattern
        if (dx * dx + dz * dz <= rd * rd) {
          const coord: ChunkCoord = { x: cx + dx, z: cz + dz };
          const key = Chunk.coordToKey(coord);
          neededChunks.add(key);

          if (!this.chunks.has(key)) {
            this.chunksToLoad.push(coord);
          }
        }
      }
    }

    // Sort by distance (load closest first)
    this.chunksToLoad.sort((a, b) => {
      const distA = (a.x - cx) ** 2 + (a.z - cz) ** 2;
      const distB = (b.x - cx) ** 2 + (b.z - cz) ** 2;
      return distA - distB;
    });

    // Find chunks to unload
    for (const key of this.chunks.keys()) {
      if (!neededChunks.has(key)) {
        this.chunksToUnload.push(key);
      }
    }
  }

  public update(): void {
    // Process chunk loading/unloading
    this.processChunkQueue();

    // Rebuild dirty chunks
    this.rebuildDirtyChunks();
  }

  private processChunkQueue(): void {
    if (this.isProcessing) return;

    // Unload chunks first
    while (this.chunksToUnload.length > 0) {
      const key = this.chunksToUnload.pop()!;
      this.unloadChunk(key);
    }

    // Load a few chunks per frame
    const maxLoadsPerFrame = 2;
    let loaded = 0;

    while (this.chunksToLoad.length > 0 && loaded < maxLoadsPerFrame) {
      const coord = this.chunksToLoad.shift()!;
      const key = Chunk.coordToKey(coord);

      if (!this.chunks.has(key)) {
        this.loadChunk(coord);
        loaded++;
      }
    }
  }

  private loadChunk(coord: ChunkCoord): void {
    const chunk = new Chunk(this.scene, coord);

    // Generate terrain
    this.terrainGenerator.generateChunk(chunk);

    // Store chunk
    const key = Chunk.coordToKey(coord);
    this.chunks.set(key, chunk);

    // Build mesh immediately
    this.buildChunkMesh(chunk);
  }

  private unloadChunk(key: string): void {
    const chunk = this.chunks.get(key);
    if (chunk) {
      chunk.dispose();
      this.chunks.delete(key);
    }
  }

  private buildChunkMesh(chunk: Chunk): void {
    const getNeighborBlock = (worldX: number, worldY: number, worldZ: number): number => {
      return this.getBlock(worldX, worldY, worldZ);
    };

    const mesh = VoxelMesher.buildMesh(chunk, getNeighborBlock);
    chunk.setMesh(mesh);
    chunk.markClean();
  }

  private rebuildDirtyChunks(): void {
    for (const chunk of this.chunks.values()) {
      if (chunk.needsRebuild()) {
        this.buildChunkMesh(chunk);
      }
    }
  }

  public getChunk(coord: ChunkCoord): Chunk | undefined {
    return this.chunks.get(Chunk.coordToKey(coord));
  }

  public getChunkAt(worldX: number, worldZ: number): Chunk | undefined {
    const coord = Chunk.worldToChunkCoord(worldX, worldZ);
    return this.getChunk(coord);
  }

  public getBlock(worldX: number, worldY: number, worldZ: number): number {
    if (worldY < 0 || worldY >= CHUNK_HEIGHT) {
      return Block.AIR;
    }

    const chunk = this.getChunkAt(worldX, worldZ);
    if (!chunk) {
      return Block.AIR;
    }

    const local = Chunk.worldToLocalCoord(worldX, worldY, worldZ);
    return chunk.getBlock(local.x, local.y, local.z);
  }

  public setBlock(worldX: number, worldY: number, worldZ: number, blockId: number): boolean {
    if (worldY < 0 || worldY >= CHUNK_HEIGHT) {
      return false;
    }

    const chunk = this.getChunkAt(worldX, worldZ);
    if (!chunk) {
      return false;
    }

    const local = Chunk.worldToLocalCoord(worldX, worldY, worldZ);
    chunk.setBlock(local.x, local.y, local.z, blockId);

    // Mark neighboring chunks dirty if block is at edge
    this.markNeighborsDirty(worldX, worldZ, local.x, local.z);

    return true;
  }

  private markNeighborsDirty(worldX: number, worldZ: number, localX: number, localZ: number): void {
    const neighborOffsets: [number, number][] = [];

    if (localX === 0) neighborOffsets.push([-CHUNK_SIZE, 0]);
    if (localX === CHUNK_SIZE - 1) neighborOffsets.push([CHUNK_SIZE, 0]);
    if (localZ === 0) neighborOffsets.push([0, -CHUNK_SIZE]);
    if (localZ === CHUNK_SIZE - 1) neighborOffsets.push([0, CHUNK_SIZE]);

    for (const [dx, dz] of neighborOffsets) {
      const neighbor = this.getChunkAt(worldX + dx, worldZ + dz);
      if (neighbor) {
        neighbor.markDirty();
      }
    }
  }

  public getHeightAt(worldX: number, worldZ: number): number {
    // First check if chunk is loaded
    const chunk = this.getChunkAt(worldX, worldZ);
    if (chunk) {
      const local = Chunk.worldToLocalCoord(worldX, 0, worldZ);
      for (let y = CHUNK_HEIGHT - 1; y >= 0; y--) {
        if (Block.isSolid(chunk.getBlock(local.x, y, local.z))) {
          return y + 1;
        }
      }
      return 0;
    }

    // Fallback to terrain generator
    return this.terrainGenerator.getHeightAt(worldX, worldZ) + 1;
  }

  public getLoadedChunkCount(): number {
    return this.chunks.size;
  }

  public getQueuedChunkCount(): number {
    return this.chunksToLoad.length;
  }

  public getRenderDistance(): number {
    return this.renderDistance;
  }

  public setRenderDistance(distance: number): void {
    this.renderDistance = Math.max(1, Math.min(16, distance));
    this.updateChunkQueue();
  }

  public forceReloadAllChunks(): void {
    // Unload all
    for (const key of this.chunks.keys()) {
      this.chunksToUnload.push(key);
    }
    this.chunksToLoad = [];

    // Rebuild queue
    this.updateChunkQueue();
  }

  public dispose(): void {
    for (const chunk of this.chunks.values()) {
      chunk.dispose();
    }
    this.chunks.clear();
    this.chunksToLoad = [];
    this.chunksToUnload = [];
  }
}
